/**
 * RespostaFornecedorForm - Formulário para registrar resposta de fornecedor em cotação
 * @version 2.2.0 - Fix fornecedor nome display with getFornecedorNome (2026-02-25)
 * 
 * Props:
 * - cotacao: objeto da cotação
 * - onSave: callback para salvar resposta
 * - onCancel: callback para cancelar
 */
import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import api from '../../lib/axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { DialogFooter, Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, Plus, Search, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { API, getAuthHeader, formatCurrency as _fmtCurrency, getFornecedorNome } from './utils';
import { CurrencyInput } from '../ui/currency-input';
import { getCurrencyMeta } from '../../lib/currencies';
import RespostaAnexosManager from './RespostaAnexosManager';
import { downloadAuthed } from '../../lib/downloadAuthed';
import PdfPreviewDialog from '../PdfPreviewDialog';
import AvaliarAlternativaDialog from './AvaliarAlternativaDialog';
import ExtrairRespostaIADialog from './ExtrairRespostaIADialog';
import { INCOTERMS } from '../../lib/tradeConstants';

const RespostaFornecedorForm = ({ cotacao: cotacaoProp, onSave, onCancel, onCotacaoChange, onAllSaved, onPendingChange }) => {
  // Estado local da cotação (permite adicionar fornecedor sem fechar o modal)
  const [cotacao, setCotacao] = useState(cotacaoProp);
  useEffect(() => { setCotacao(cotacaoProp); }, [cotacaoProp?.id]);
  // Valores exibidos na moeda da cotação (sem conversão)
  const formatCurrency = (v) => _fmtCurrency(v, cotacao?.moeda || 'BRL');

  const [selectedFornecedor, setSelectedFornecedor] = useState('');
  const [fornecedoresDisponiveis, setFornecedoresDisponiveis] = useState([]);

  // Cache de respostas em memória, por fornecedor_id.
  // Permite o usuário alternar entre fornecedores sem perder o que já preencheu.
  // O "Salvar Resposta" persiste todos os fornecedores que tiveram alterações.
  const [respostasPorFornecedor, setRespostasPorFornecedor] = useState({});
  const [respostasAlteradas, setRespostasAlteradas] = useState(new Set());
  const previousFornecedorRef = useRef(null);

  // Notifica o pai sobre alterações pendentes em memória
  useEffect(() => {
    if (onPendingChange) onPendingChange(respostasAlteradas.size);
  }, [respostasAlteradas, onPendingChange]);

  const VALOR_PADRAO_FORM = {
    itens: [],
    condicao_pagamento: '',
    forma_pagamento: '',
    prazo_garantia: '',
    prazo_entrega: '',
    frete_tipo: 'cif',
    frete_valor: 0,
    frete_minimo: 0,
    validade_proposta: '',
    impostos_inclusos: true,
    valor_impostos: 0,
    desconto: 0,
    assistencia_tecnica: '',
    observacao: '',
    incoterm: '',
    porto_origem: '',
    transit_time_dias: '',
    peso_bruto: '',
    peso_liquido: '',
    quantidade_volumes: '',
    volume_cbm: '',
    dimensoes_observacao: '',
  };
  const [respostaForm, setRespostaForm] = useState(VALOR_PADRAO_FORM);
  const logisticaEstimadaRef = useRef({});
  const [loading, setLoading] = useState(false);
  const [alternativaEditando, setAlternativaEditando] = useState(null); // idx do item sendo editado
  const [pdfPreviewResposta, setPdfPreviewResposta] = useState(null); // { path, filename, title }
  const [avaliarAlternativa, setAvaliarAlternativa] = useState(null); // { itemIdx, item, fornecedor, info }
  const [showAddFornecedor, setShowAddFornecedor] = useState(false);
  const [showExtrairIA, setShowExtrairIA] = useState(false);
  const [todosFornecedores, setTodosFornecedores] = useState([]);
  // Indicador de catálogo: { [fornecedor_id]: number_de_catalogos }
  const [catalogosCount, setCatalogosCount] = useState({});

  // Fornecedores que ainda não responderam (da cotação) E os que já responderam (modo atualizar)
  const fornecedoresDaCotacao = cotacao.fornecedores || [];
  const fornecedoresPendentes = fornecedoresDaCotacao.filter(f => f.status !== 'respondido');
  const fornecedoresRespondidos = fornecedoresDaCotacao.filter(f => f.status === 'respondido');

  // Carregar nomes completos dos fornecedores da cotação + lista geral
  useEffect(() => {
    const fetchFornecedores = async () => {
      try {
        const response = await api.get('/fornecedores');
        const data = response.data;
        const lista = Array.isArray(data) ? data : (data?.items || []);
        setTodosFornecedores(lista);

        // Mapear contagem de catálogos por fornecedor (vem do cadastro)
        const counts = {};
        lista.forEach(f => {
          counts[f.id] = Array.isArray(f.catalogos) ? f.catalogos.length : 0;
        });
        setCatalogosCount(counts);

        const fornecedoresComNome = fornecedoresDaCotacao.map(fp => {
          const fornecedorCompleto = lista.find(f => f.id === fp.fornecedor_id);
          const nomeExibicao = fp.fornecedor_nome?.trim()
            ? fp.fornecedor_nome
            : (fornecedorCompleto ? getFornecedorNome(fornecedorCompleto) : `Fornecedor ${fp.fornecedor_id?.substring(0, 8)}...`);
          return { ...fp, fornecedor_nome: nomeExibicao };
        });
        setFornecedoresDisponiveis(fornecedoresComNome);
      } catch (error) {
        setFornecedoresDisponiveis(fornecedoresDaCotacao);
      }
    };
    fetchFornecedores();
  }, [cotacao.id, cotacao.fornecedores?.length]);

  // Estimativa de logística (soma do cadastro dos produtos) para pré-preencher o bloco de frete
  useEffect(() => {
    if (!cotacao?.id || cotacao?.moeda === 'BRL') return;
    api.get(`/cotacoes/${cotacao.id}/logistica-estimada`)
      .then((r) => {
        if (r.data?.tem_dados) {
          logisticaEstimadaRef.current = r.data;
          // Aplica como default se o form atual ainda estiver vazio nesses campos
          setRespostaForm((prev) => {
            if (prev.peso_bruto || prev.volume_cbm || prev.peso_liquido || prev.quantidade_volumes) return prev;
            return {
              ...prev,
              peso_bruto: r.data.peso_bruto || '',
              peso_liquido: r.data.peso_liquido || '',
              quantidade_volumes: r.data.quantidade_volumes || '',
              volume_cbm: r.data.volume_cbm || '',
            };
          });
        }
      })
      .catch(() => {});
  }, [cotacao?.id]); // eslint-disable-line

  // Lista final de fornecedores para seleção: SEMPRE os da cotação
  const fornecedoresParaSelecao = fornecedoresDisponiveis.length > 0
    ? fornecedoresDisponiveis
    : fornecedoresDaCotacao;

  // Helper: monta o respostaForm inicial pra um fornecedor (usa resposta anterior se houver)
  const construirFormParaFornecedor = (fornecedorId, cotacaoOverride = null) => {
    const cot = cotacaoOverride || cotacao;
    const fornEntry = (cot.fornecedores || []).find(f => f.fornecedor_id === fornecedorId);
    const respostaAnterior = fornEntry?.resposta || null;
    const itensAnteriores = respostaAnterior?.itens || [];
    const itensAtualizados = (cot.itens || []).map(item => {
      const ja = itensAnteriores.find(i => i.item_id === item.id);
      const equivalente = ja?.equivalente || null;
      return {
        item_id: item.id,
        descricao: item.descricao, // SEMPRE o item original da cotação
        quantidade: item.quantidade,
        preco_unitario: ja?.preco_unitario || 0,
        marca_ofertada: ja?.marca_ofertada || item.marca || '',
        modelo_ofertado: ja?.modelo_ofertado || item.modelo || '',
        prazo_entrega_dias: ja?.prazo_entrega_dias || null,
        disponibilidade: ja?.disponibilidade !== undefined ? ja.disponibilidade : true,
        observacao: ja?.observacao || '',
        // Alternativa sugerida pelo fornecedor (texto livre)
        descricao_alternativa: ja?.descricao_alternativa || '',
        motivo_alternativa: ja?.motivo_alternativa || '',
        marca_alternativa: ja?.marca_alternativa || '',
        // Equivalente ADOTADO pelo comprador (POR fornecedor — vive na resposta)
        equivalente_adotado: !!ja?.equivalente_adotado,
        equivalente: equivalente,
      };
    });
    return {
      itens: itensAtualizados,
      condicao_pagamento: respostaAnterior?.condicao_pagamento || '',
      forma_pagamento: respostaAnterior?.forma_pagamento || '',
      prazo_garantia: respostaAnterior?.prazo_garantia || '',
      prazo_entrega: respostaAnterior?.prazo_entrega || '',
      frete_tipo: respostaAnterior?.frete_tipo || 'cif',
      frete_valor: respostaAnterior?.frete_valor || 0,
      frete_minimo: respostaAnterior?.frete_minimo || 0,
      validade_proposta: respostaAnterior?.validade_proposta || '',
      impostos_inclusos: respostaAnterior?.impostos_inclusos !== undefined ? respostaAnterior.impostos_inclusos : true,
      valor_impostos: respostaAnterior?.valor_impostos || 0,
      desconto: respostaAnterior?.desconto || 0,
      assistencia_tecnica: respostaAnterior?.assistencia_tecnica || '',
      observacao: respostaAnterior?.observacao || '',
      // Condições de importação — pré-preenchidas com os defaults da cotação (fornecedor pode sobrescrever)
      incoterm: respostaAnterior?.incoterm || cot?.incoterm || '',
      porto_origem: respostaAnterior?.porto_origem || cot?.porto_origem || '',
      transit_time_dias: respostaAnterior?.transit_time_dias ?? cot?.transit_time_dias ?? '',
      // Logística / Frete — resposta salva > estimativa do cadastro dos produtos
      peso_bruto: respostaAnterior?.peso_bruto ?? (logisticaEstimadaRef.current?.peso_bruto || '') ,
      peso_liquido: respostaAnterior?.peso_liquido ?? (logisticaEstimadaRef.current?.peso_liquido || ''),
      quantidade_volumes: respostaAnterior?.quantidade_volumes ?? (logisticaEstimadaRef.current?.quantidade_volumes || ''),
      volume_cbm: respostaAnterior?.volume_cbm ?? (logisticaEstimadaRef.current?.volume_cbm || ''),
      dimensoes_observacao: respostaAnterior?.dimensoes_observacao || '',
    };
  };

  // Ao trocar de fornecedor: preserva o que estava sendo editado em memória
  // e carrega o cache do novo (ou monta do zero usando resposta anterior do banco).
  useEffect(() => {
    if (!selectedFornecedor) {
      previousFornecedorRef.current = null;
      return;
    }
    const prevForn = previousFornecedorRef.current;
    if (prevForn && prevForn !== selectedFornecedor) {
      // Preserva apenas em cache local — NÃO marcar como "alterado".
      // O marcador de alteração só é setado em setRespostaFormAndMark
      // quando o usuário realmente edita um campo (linha ~190).
      setRespostasPorFornecedor(prev => ({ ...prev, [prevForn]: respostaForm }));
    }
    // Carrega resposta do novo fornecedor: cache > resposta antiga do banco > vazio
    const cached = respostasPorFornecedor[selectedFornecedor];
    if (cached) {
      setRespostaForm(cached);
    } else {
      setRespostaForm(construirFormParaFornecedor(selectedFornecedor));
    }
    previousFornecedorRef.current = selectedFornecedor;
    // deps mantidas explícitas; demais são lidas de fechamento (acessíveis no momento)
  }, [selectedFornecedor, cotacao.itens]);

  const getNomeFornecedor = (fornId) => {
    const f = fornecedoresParaSelecao.find(x => x.fornecedor_id === fornId);
    return f?.fornecedor_nome || `Fornecedor ${fornId?.substring(0, 8)}`;
  };

  // Marca a resposta atual como "alterada" sempre que o usuário edita algo
  const setRespostaFormAndMark = (updater) => {
    setRespostaForm(updater);
    if (selectedFornecedor) {
      setRespostasAlteradas(prev => new Set(prev).add(selectedFornecedor));
    }
  };

  const updateItemResposta = (index, field, value) => {
    setRespostaFormAndMark(prev => ({
      ...prev,
      itens: prev.itens.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const calcularTotais = () => {
    const subtotal = respostaForm.itens.reduce((sum, item) => {
      return sum + ((item.preco_unitario || 0) * (item.quantidade || 0));
    }, 0);
    const frete = parseFloat(respostaForm.frete_valor) || 0;
    const impostos = respostaForm.impostos_inclusos ? 0 : (parseFloat(respostaForm.valor_impostos) || 0);
    const desconto = parseFloat(respostaForm.desconto) || 0;
    const total = subtotal + frete + impostos - desconto;
    return { subtotal, frete, impostos, desconto, total };
  };
  const calcularTotal = () => calcularTotais().total;

  // Adiciona um fornecedor à cotação atual (PUT na cotação) e seleciona automaticamente
  const handleAdicionarFornecedor = async (fornecedor) => {
    if (!fornecedor) return;
    if ((cotacao.fornecedores || []).some(f => f.fornecedor_id === fornecedor.id)) {
      toast.error('Esse fornecedor já está vinculado à cotação');
      return;
    }
    try {
      const novoForn = {
        fornecedor_id: fornecedor.id,
        fornecedor_nome: getFornecedorNome(fornecedor),
        contato_email: fornecedor.email || '',
        status: 'pendente',
      };
      const fornecedoresAtualizados = [...(cotacao.fornecedores || []), novoForn];
      const resp = await api.put(`/cotacoes/${cotacao.id}`, { fornecedores: fornecedoresAtualizados });
      const cotacaoAtualizada = resp.data;
      setCotacao(cotacaoAtualizada);
      if (onCotacaoChange) onCotacaoChange(cotacaoAtualizada);
      setShowAddFornecedor(false);
      toast.success(`${getFornecedorNome(fornecedor)} adicionado à cotação`);
      // Pre-seleciona o novo fornecedor para já preencher
      setSelectedFornecedor(fornecedor.id);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Erro ao adicionar fornecedor');
    }
  };

  const handleSubmit = async () => {
    if (!selectedFornecedor) {
      toast.error('Selecione um fornecedor');
      return;
    }
    // Garante que a resposta atual entra no map antes de iterar
    const todasRespostas = {
      ...respostasPorFornecedor,
      [selectedFornecedor]: respostaForm,
    };

    // Helper: respota tem ao menos 1 preço preenchido?
    const temPrecos = (r) => !!r && Array.isArray(r.itens) && r.itens.some(i => (i.preco_unitario || 0) > 0);

    // Lista de fornecedores a salvar = todos os alterados.
    // Inclui o atualmente selecionado SOMENTE se ele tiver alteração marcada
    // OU se tiver pelo menos 1 preço (caso o user editou e nunca trocou de fornecedor).
    const idsParaSalvar = new Set(respostasAlteradas);
    if (respostasAlteradas.has(selectedFornecedor) || temPrecos(respostaForm)) {
      idsParaSalvar.add(selectedFornecedor);
    }

    if (idsParaSalvar.size === 0) {
      toast.error('Nenhuma resposta foi preenchida. Informe pelo menos 1 preço para salvar.');
      return;
    }

    // Validação: cada resposta a salvar precisa ter pelo menos 1 preço > 0
    for (const fornId of idsParaSalvar) {
      const r = todasRespostas[fornId];
      if (!temPrecos(r)) {
        toast.error(`Resposta de "${getNomeFornecedor(fornId)}" sem preços. Preencha pelo menos 1 item.`);
        return;
      }
    }

    setLoading(true);
    try {
      // Salva sequencialmente cada resposta alterada (TODAS em modo silent — sem toast individual)
      for (const fornId of idsParaSalvar) {
        await onSave(fornId, todasRespostas[fornId], { silent: true });
      }
      const total = idsParaSalvar.size;
      toast.success(total > 1
        ? `${total} respostas salvas com sucesso!`
        : 'Resposta registrada!');
      setRespostasPorFornecedor({});
      setRespostasAlteradas(new Set());
      // Sinaliza fechamento + refresh para o pai
      if (onAllSaved) onAllSaved();
      else if (onCancel) onCancel();
    } finally {
      setLoading(false);
    }
  };

  // Adotar alternativa "inline" no form de resposta:
  // 1. Persiste a resposta atual (com a alternativa) se houver pendência
  // 2. Abre o AvaliarAlternativaDialog para o comprador escolher vincular/cadastrar/texto livre
  const handleAdotarInline = async (itemIdx, item) => {
    if (!selectedFornecedor) {
      toast.error('Selecione um fornecedor primeiro');
      return;
    }
    if (!item?.descricao_alternativa?.trim()) {
      toast.error('Preencha a descrição da alternativa primeiro');
      return;
    }
    // Salva a resposta atual primeiro (silenciosa) — necessário pq o backend lê a sugestão do banco
    try {
      if (respostasAlteradas.size > 0 && onSave) {
        const todasRespostas = { ...respostasPorFornecedor, [selectedFornecedor]: respostaForm };
        const ids = Array.from(respostasAlteradas);
        // garante que o atual entre na fila se foi modificado
        if (!ids.includes(selectedFornecedor) && respostasAlteradas.has(selectedFornecedor)) {
          ids.push(selectedFornecedor);
        }
        // Persiste o atual
        if (!ids.includes(selectedFornecedor)) ids.push(selectedFornecedor);
        for (const fid of ids) {
          await onSave(fid, todasRespostas[fid] || respostaForm, { silent: true });
        }
        setRespostasAlteradas(new Set());
        toast.success('Resposta salva — abrindo assistente de adoção');
      } else if (!fornecedoresDaCotacao.find(f => f.fornecedor_id === selectedFornecedor)?.resposta) {
        // Nenhuma resposta salva ainda — salva pela primeira vez
        await onSave(selectedFornecedor, respostaForm, { silent: true });
        toast.success('Resposta salva — abrindo assistente de adoção');
      }
    } catch (e) {
      toast.error('Erro ao salvar resposta antes de adotar');
      return;
    }
    // Abre o dialog com os dados da alternativa
    const forn = fornecedoresDaCotacao.find(f => f.fornecedor_id === selectedFornecedor) || {};
    setAvaliarAlternativa({
      itemIdx,
      item: { ...item, descricao: item.descricao, quantidade: item.quantidade, unidade: item.unidade },
      fornecedor: { fornecedor_id: selectedFornecedor, fornecedor_nome: forn.fornecedor_nome || getNomeFornecedor(selectedFornecedor) },
      info: {
        preco_unitario: item.preco_unitario || 0,
        descricao_alternativa: item.descricao_alternativa,
        marca_alternativa: item.marca_alternativa,
        motivo_alternativa: item.motivo_alternativa,
      },
    });
  };

  // Aplica o resultado revisado da extração por IA no formulário (rascunho — nada salvo ainda)
  // IMPORTANTE: calcula o merge de forma síncrona e grava também no cache respostasPorFornecedor,
  // pois o useEffect [selectedFornecedor, cotacao.itens] re-executa quando novos itens são
  // adicionados à cotação e recarrega o form a partir do cache — sem isso, os itens novos e
  // os valores aplicados eram descartados.
  const aplicarExtracaoIA = (payload) => {
    const { itens: itensRevisados = [], condicoes = {}, novosItens = [], substituicoes = [], cotacaoAtualizada } = payload;

    let itens = [...respostaForm.itens];

    // Acrescenta linhas para os novos itens da cotação (já com vínculo de produto quando houver)
    novosItens.forEach(n => {
      if (!itens.some(i => i.item_id === n.id)) {
        itens.push({
          item_id: n.id,
          descricao: n.descricao,
          quantidade: n.quantidade,
          preco_unitario: n.preco_unitario || 0,
          marca_ofertada: '',
          modelo_ofertado: '',
          prazo_entrega_dias: null,
          disponibilidade: true,
          observacao: '',
          descricao_alternativa: '',
          motivo_alternativa: '',
          marca_alternativa: '',
          equivalente_adotado: false,
          equivalente: null,
        });
      }
    });

    // Aplica valores revisados pelo usuário + substituições decididas
    itens = itens.map(it => {
      const upd = { ...it };
      const rev = itensRevisados.find(r => r.item_id === it.item_id);
      if (rev) {
        if (rev.preco !== '' && rev.preco != null) upd.preco_unitario = parseFloat(rev.preco) || 0;
        if (rev.prazo !== '' && rev.prazo != null) upd.prazo_entrega_dias = parseInt(rev.prazo) || null;
        upd.disponibilidade = rev.disponivel !== false;
        if (rev.marca_ofertada) upd.marca_ofertada = rev.marca_ofertada;
        if (rev.substituicao_sugerida && !upd.descricao_alternativa) {
          upd.descricao_alternativa = rev.substituicao_sugerida;
        }
      }
      const sub = substituicoes.find(s => s.item_id === it.item_id);
      if (sub) {
        upd.descricao_alternativa = sub.descricao;
        upd.motivo_alternativa = 'Oferecido pelo fornecedor (leitura IA)';
        if (sub.preco != null && (!upd.preco_unitario || upd.preco_unitario === 0)) {
          upd.preco_unitario = sub.preco;
        }
      }
      return upd;
    });

    const merged = {
      ...respostaForm,
      itens,
      condicao_pagamento: condicoes.condicao_pagamento || respostaForm.condicao_pagamento,
      forma_pagamento: condicoes.forma_pagamento || respostaForm.forma_pagamento,
      prazo_entrega: condicoes.prazo_entrega || respostaForm.prazo_entrega,
      prazo_garantia: condicoes.prazo_garantia || respostaForm.prazo_garantia,
      frete_tipo: condicoes.frete_tipo || respostaForm.frete_tipo,
      frete_valor: condicoes.frete_valor ?? respostaForm.frete_valor,
      validade_proposta: condicoes.validade_proposta || respostaForm.validade_proposta,
      impostos_inclusos: condicoes.impostos_inclusos ?? respostaForm.impostos_inclusos,
      desconto: condicoes.desconto || respostaForm.desconto,
      assistencia_tecnica: condicoes.assistencia_tecnica || respostaForm.assistencia_tecnica,
      observacao: condicoes.observacao
        ? `${respostaForm.observacao ? respostaForm.observacao + '\n' : ''}${condicoes.observacao}`
        : respostaForm.observacao,
    };

    setRespostaForm(merged);
    if (selectedFornecedor) {
      setRespostasPorFornecedor(prev => ({ ...prev, [selectedFornecedor]: merged }));
      setRespostasAlteradas(prev => new Set(prev).add(selectedFornecedor));
    }

    // Atualiza a cotação por último: dispara o useEffect que recarrega o form do cache (já com merge)
    if (cotacaoAtualizada) {
      setCotacao(cotacaoAtualizada);
      if (onCotacaoChange) onCotacaoChange(cotacaoAtualizada);
    }
  };

  return (
    <div className="space-y-6 mt-4">
      {/* Seleção de Fornecedor */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <Label>Fornecedor *</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowAddFornecedor(true)}
            data-testid="btn-add-fornecedor-resposta"
          >
            <Plus className="w-3 h-3 mr-1" />
            Adicionar fornecedor à cotação
          </Button>
        </div>
        <Select value={selectedFornecedor} onValueChange={setSelectedFornecedor}>
          <SelectTrigger data-testid="select-fornecedor-resposta">
            <SelectValue placeholder="Selecione o fornecedor que respondeu" />
          </SelectTrigger>
          <SelectContent>
            {fornecedoresParaSelecao.map(forn => {
              const jaRespondeu = forn.status === 'respondido';
              const temAlteracao = respostasAlteradas.has(forn.fornecedor_id) && forn.fornecedor_id !== selectedFornecedor;
              const numCatalogos = catalogosCount[forn.fornecedor_id] || 0;
              // Calcula status detalhado (Respondido / Parcial / Aguardando)
              const totalItens = (cotacao.itens || []).length;
              const itensRespondidos = jaRespondeu
                ? (forn.resposta?.itens || []).filter(ir => (ir.preco_unitario || 0) > 0).length
                : 0;
              let statusTag = null;
              if (!jaRespondeu) {
                statusTag = { label: 'Aguardando Resposta', cls: 'bg-amber-100 text-amber-800 border-amber-300' };
              } else if (itensRespondidos < totalItens && totalItens > 0) {
                statusTag = { label: `Respondido Parcialmente (${itensRespondidos}/${totalItens})`, cls: 'bg-orange-100 text-orange-800 border-orange-300' };
              } else {
                statusTag = { label: 'Respondido', cls: 'bg-emerald-100 text-emerald-800 border-emerald-300' };
              }
              return (
                <SelectItem key={forn.fornecedor_id} value={forn.fornecedor_id} data-testid={`opt-forn-${forn.fornecedor_id}`}>
                  <span className="flex items-center gap-2 flex-wrap">
                    <span>{forn.fornecedor_nome || `Fornecedor ${forn.fornecedor_id?.substring(0, 8)}...`}</span>
                    <span
                      className={`text-[10px] px-1.5 py-0.5 rounded border ${statusTag.cls}`}
                      data-testid={`tag-status-${forn.fornecedor_id}`}
                    >
                      {statusTag.label}
                    </span>
                    {temAlteracao && <span className="text-xs text-amber-600">· alterações pendentes</span>}
                    {numCatalogos > 0 && (
                      <span className="text-xs text-emerald-600" title={`${numCatalogos} catálogo(s) cadastrado(s)`}>
                        📕 {numCatalogos > 1 ? `×${numCatalogos}` : ''}
                      </span>
                    )}
                  </span>
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
        {fornecedoresParaSelecao.length === 0 && (
          <p className="text-sm text-amber-600 mt-2 flex items-start gap-2">
            <span>⚠️</span>
            <span>
              Esta cotação ainda não tem fornecedores vinculados. Use o botão <strong>"Adicionar fornecedor à cotação"</strong> acima.
            </span>
          </p>
        )}
        {selectedFornecedor && fornecedoresRespondidos.some(f => f.fornecedor_id === selectedFornecedor) && (
          <div className="flex items-center justify-between gap-2 mt-2 bg-blue-50 border border-blue-200 rounded px-3 py-2">
            <p className="text-sm text-blue-700 flex-1">
              ℹ️ Este fornecedor já tinha uma resposta registrada. Os preços anteriores foram pré-preenchidos.
            </p>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="bg-white border-blue-300 text-blue-700 hover:bg-blue-100"
              onClick={() => setPdfPreviewResposta({
                path: `/cotacoes/${cotacao.id}/resposta/${selectedFornecedor}/pdf`,
                filename: `Resposta_${cotacao.numero}_${getNomeFornecedor(selectedFornecedor).replace(/\s+/g,'_')}.pdf`,
                title: `Resposta — ${getNomeFornecedor(selectedFornecedor)} · ${cotacao.numero}`,
              })}
              data-testid="btn-pdf-resposta"
            >
              📄 PDF da Resposta
            </Button>
          </div>
        )}
        {respostasAlteradas.size > 0 && (
          <p className="text-sm text-amber-700 mt-2 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            💾 <strong>{respostasAlteradas.size} fornecedor(es) com alterações pendentes</strong> em memória. Clique em "Salvar Resposta" no fim para persistir tudo de uma vez.
          </p>
        )}
      </div>

      {selectedFornecedor && (
        <>
          {/* Preencher com IA */}
          <div className="flex items-center justify-between gap-3 flex-wrap p-3 rounded-lg bg-indigo-50 border border-indigo-200">
            <p className="text-xs text-indigo-800 flex-1 min-w-[220px]">
              <Sparkles className="w-3.5 h-3.5 inline mr-1" />
              O fornecedor respondeu por e-mail, WhatsApp ou PDF? Deixe a IA preencher o rascunho — você só confere.
            </p>
            <Button
              type="button"
              size="sm"
              onClick={() => setShowExtrairIA(true)}
              className="bg-indigo-600 hover:bg-indigo-700 h-8 text-xs shrink-0"
              data-testid="btn-preencher-ia"
            >
              <Sparkles className="w-3.5 h-3.5 mr-1" />
              Preencher com IA
            </Button>
          </div>

          {/* Preços dos Itens */}
          <div>
            <Label className="mb-3 block">Preços dos Itens</Label>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-3">Item</th>
                    <th className="text-center p-3 w-20">Qtd</th>
                    <th className="text-right p-3 w-32">Preço Unit.</th>
                    <th className="text-right p-3 w-32">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {respostaForm.itens.map((item, idx) => (
                    <React.Fragment key={item.item_id}>
                    <tr className="border-t">
                      <td className="p-3">
                        <p className="font-medium">{item.descricao}</p>
                        {/* Equivalente JÁ ADOTADO para ESTE fornecedor */}
                        {item.equivalente_adotado && item.equivalente && (
                          <div className="mt-1 px-2 py-1 rounded bg-emerald-50 border border-emerald-200 text-[11px]" data-testid={`badge-alt-adotada-${idx}`}>
                            <span className="text-emerald-700 font-semibold">✓ Equivalente adotado para este fornecedor</span>
                            {item.equivalente.opcao === 'vincular' && item.equivalente.produto_nome && (
                              <span className="text-slate-700"> · vinculado a <strong>{item.equivalente.produto_nome}</strong></span>
                            )}
                            {item.equivalente.opcao === 'cadastrar' && item.equivalente.produto_nome && (
                              <span className="text-slate-700"> · novo produto <strong>{item.equivalente.produto_nome}</strong></span>
                            )}
                            {item.equivalente.opcao === 'texto_livre' && item.equivalente.descricao && (
                              <span className="text-slate-700"> · <strong>{item.equivalente.descricao}</strong> (texto livre)</span>
                            )}
                          </div>
                        )}
                        {item.descricao_alternativa && !item.equivalente_adotado && (
                          <div className="mt-1 px-2 py-1 rounded bg-orange-50 border border-orange-200 text-[11px]" data-testid={`badge-alt-${idx}`}>
                            <span className="text-orange-700 font-semibold">💡 Alternativa sugerida:</span>{' '}
                            <span className="text-slate-700">{item.descricao_alternativa}</span>
                            {item.marca_alternativa && (
                              <span className="text-slate-500"> · marca: {item.marca_alternativa}</span>
                            )}
                            {item.motivo_alternativa && (
                              <div className="text-slate-500 italic mt-0.5">Motivo: {item.motivo_alternativa}</div>
                            )}
                          </div>
                        )}
                        <button
                          type="button"
                          className="mt-1 text-[11px] text-orange-600 hover:text-orange-800 hover:underline inline-flex items-center gap-0.5"
                          onClick={() => setAlternativaEditando(alternativaEditando === idx ? null : idx)}
                          data-testid={`btn-toggle-alt-${idx}`}
                        >
                          {item.descricao_alternativa ? '✏️ Editar alternativa' : '➕ Sugerir alternativa equivalente'}
                        </button>
                      </td>
                      <td className="p-3 text-center">{item.quantidade}</td>
                      <td className="p-3">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{getCurrencyMeta(cotacao?.moeda || 'BRL').symbol}</span>
                          <Input
                            type="text"
                            inputMode="decimal"
                            value={item.preco_unitario_display !== undefined ? item.preco_unitario_display : (item.preco_unitario || '')}
                            onChange={(e) => {
                              let displayVal = e.target.value.replace(/[^\d.,]/g, '');
                              let numVal = displayVal.replace(',', '.');
                              const parts = numVal.split('.');
                              if (parts.length > 2) {
                                numVal = parts[0] + '.' + parts.slice(1).join('');
                              }
                              setRespostaFormAndMark(prev => ({
                                ...prev,
                                itens: prev.itens.map((it, i) =>
                                  i === idx ? { 
                                    ...it, 
                                    preco_unitario_display: displayVal,
                                    preco_unitario: displayVal === '' ? 0 : parseFloat(numVal) || 0 
                                  } : it
                                )
                              }));
                            }}
                            onBlur={() => {
                              const numVal = item.preco_unitario || 0;
                              setRespostaFormAndMark(prev => ({
                                ...prev,
                                itens: prev.itens.map((it, i) =>
                                  i === idx ? { 
                                    ...it, 
                                    preco_unitario_display: undefined,
                                    preco_unitario: numVal 
                                  } : it
                                )
                              }));
                            }}
                            className="text-right pl-8"
                            placeholder="0.00"
                            data-testid={`item-preco-${idx}`}
                          />
                        </div>
                      </td>
                      <td className="p-3 text-right font-medium">
                        {formatCurrency((item.preco_unitario || 0) * item.quantidade)}
                      </td>
                    </tr>
                    {alternativaEditando === idx && (
                      <tr className="border-t bg-orange-50/40" data-testid={`row-alt-edit-${idx}`}>
                        <td colSpan={4} className="p-3">
                          <div className="space-y-2">
                            <div className="text-[11px] font-semibold text-orange-700">
                              Sugerir alternativa para este item
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <Label className="text-[11px]">Descrição da alternativa <span className="text-red-500">*</span></Label>
                                <Input
                                  value={item.descricao_alternativa || ''}
                                  onChange={e => setRespostaFormAndMark(prev => ({
                                    ...prev,
                                    itens: prev.itens.map((it, i) => i === idx ? { ...it, descricao_alternativa: e.target.value } : it)
                                  }))}
                                  placeholder="Ex: Bateria Heliar 105Ah Free 24M (equivalente)"
                                  className="text-sm"
                                  data-testid={`input-alt-desc-${idx}`}
                                />
                              </div>
                              <div>
                                <Label className="text-[11px]">Marca alternativa</Label>
                                <Input
                                  value={item.marca_alternativa || ''}
                                  onChange={e => setRespostaFormAndMark(prev => ({
                                    ...prev,
                                    itens: prev.itens.map((it, i) => i === idx ? { ...it, marca_alternativa: e.target.value } : it)
                                  }))}
                                  placeholder="Ex: Heliar"
                                  className="text-sm"
                                  data-testid={`input-alt-marca-${idx}`}
                                />
                              </div>
                            </div>
                            <div>
                              <Label className="text-[11px]">Motivo / observação</Label>
                              <Textarea
                                value={item.motivo_alternativa || ''}
                                onChange={e => setRespostaFormAndMark(prev => ({
                                  ...prev,
                                  itens: prev.itens.map((it, i) => i === idx ? { ...it, motivo_alternativa: e.target.value } : it)
                                }))}
                                placeholder="Ex: Item original em falta — esta é equivalente em capacidade e ciclos de carga"
                                rows={2}
                                className="text-sm"
                                data-testid={`input-alt-motivo-${idx}`}
                              />
                            </div>
                            <div className="flex justify-end gap-2 flex-wrap">
                              {item.descricao_alternativa && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setRespostaFormAndMark(prev => ({
                                      ...prev,
                                      itens: prev.itens.map((it, i) => i === idx ? { ...it, descricao_alternativa: '', marca_alternativa: '', motivo_alternativa: '' } : it)
                                    }));
                                    setAlternativaEditando(null);
                                  }}
                                  className="border-rose-300 text-rose-600 hover:bg-rose-50 text-[11px] h-7"
                                  data-testid={`btn-alt-remover-${idx}`}
                                >
                                  Remover alternativa
                                </Button>
                              )}
                              {item.descricao_alternativa && (
                                <Button
                                  type="button"
                                  size="sm"
                                  onClick={() => handleAdotarInline(idx, item)}
                                  className="bg-emerald-600 hover:bg-emerald-700 text-[11px] h-7"
                                  data-testid={`btn-alt-adotar-${idx}`}
                                  title="Salva a resposta e abre o assistente para vincular/cadastrar produto"
                                >
                                  ✅ Adotar como produto agora
                                </Button>
                              )}
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => setAlternativaEditando(null)}
                                className="bg-orange-500 hover:bg-orange-600 text-[11px] h-7"
                                data-testid={`btn-alt-ok-${idx}`}
                              >
                                Concluir
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  ))}
                </tbody>
                <tfoot className="bg-gray-50">
                  {(() => {
                    const t = calcularTotais();
                    return (
                      <>
                        <tr className="border-t">
                          <td colSpan={3} className="p-3 text-right text-sm text-gray-600">Subtotal:</td>
                          <td className="p-3 text-right font-medium">{formatCurrency(t.subtotal)}</td>
                        </tr>
                        {t.frete > 0 && (
                          <tr>
                            <td colSpan={3} className="p-3 text-right text-sm text-gray-600">+ Frete:</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(t.frete)}</td>
                          </tr>
                        )}
                        {t.impostos > 0 && (
                          <tr>
                            <td colSpan={3} className="p-3 text-right text-sm text-gray-600">+ Impostos:</td>
                            <td className="p-3 text-right font-medium">{formatCurrency(t.impostos)}</td>
                          </tr>
                        )}
                        {t.desconto > 0 && (
                          <tr>
                            <td colSpan={3} className="p-3 text-right text-sm text-red-600">− Desconto:</td>
                            <td className="p-3 text-right font-medium text-red-600">−{formatCurrency(t.desconto)}</td>
                          </tr>
                        )}
                        <tr className="border-t-2 border-gray-300">
                          <td colSpan={3} className="p-3 text-right font-medium">Total:</td>
                          <td className="p-3 text-right font-bold text-lg text-green-600" data-testid="resposta-total">
                            {formatCurrency(t.total)}
                          </td>
                        </tr>
                      </>
                    );
                  })()}
                </tfoot>
              </table>
            </div>
          </div>

          {/* Condições */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Frete (CIF ou FOB)</Label>
              <Select
                value={respostaForm.frete_tipo}
                onValueChange={(v) => setRespostaFormAndMark(prev => ({ ...prev, frete_tipo: v }))}
              >
                <SelectTrigger data-testid="resposta-frete-tipo">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cif">CIF (Frete Incluso)</SelectItem>
                  <SelectItem value="fob">FOB (Frete por conta do comprador)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Valor Frete</Label>
              <CurrencyInput
                value={respostaForm.frete_valor || 0}
                onChange={(v) => setRespostaFormAndMark(prev => ({ ...prev, frete_valor: v }))}
                placeholder="0,00"
                currency={cotacao?.moeda || 'BRL'}
                disabled={respostaForm.frete_tipo === 'cif'}
                data-testid="resposta-frete-valor"
              />
            </div>
            <div>
              <Label title="Valor mínimo de pedido aceito pelo fornecedor (gera alerta ao comparar se a soma dos itens escolhidos ficar abaixo deste valor)">
                Valor de pedido mínimo
              </Label>
              <CurrencyInput
                value={respostaForm.frete_minimo || 0}
                onChange={(v) => setRespostaFormAndMark(prev => ({ ...prev, frete_minimo: v }))}
                placeholder="0,00"
                currency={cotacao?.moeda || 'BRL'}
                data-testid="resposta-frete-minimo"
              />
            </div>
            <div>
              <Label>Forma de Pagamento</Label>
              <Select
                value={respostaForm.forma_pagamento || ''}
                onValueChange={(v) => setRespostaFormAndMark(prev => ({ ...prev, forma_pagamento: v }))}
              >
                <SelectTrigger data-testid="resposta-forma-pagamento">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a_vista">À Vista</SelectItem>
                  <SelectItem value="pix">PIX</SelectItem>
                  <SelectItem value="boleto">Boleto</SelectItem>
                  <SelectItem value="cartao">Cartão</SelectItem>
                  <SelectItem value="parcelado">Parcelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Condição/Parcelas</Label>
              <Input
                value={respostaForm.condicao_pagamento || ''}
                onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, condicao_pagamento: e.target.value }))}
                placeholder="Ex: 3x, 30/60/90, À vista"
                data-testid="resposta-condicao-pagamento"
              />
              <p className="text-xs text-gray-500 mt-1">Digite livremente: 3x, 30/60/90 dias, etc.</p>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <Label>Impostos</Label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm">R$</span>
                <Input
                  type="text"
                  inputMode="decimal"
                  value={respostaForm.valor_impostos_display !== undefined ? respostaForm.valor_impostos_display : (respostaForm.valor_impostos || '')}
                  onChange={(e) => {
                    let displayVal = e.target.value.replace(/[^\d.,]/g, '');
                    let numVal = displayVal.replace(',', '.');
                    const parts = numVal.split('.');
                    if (parts.length > 2) {
                      numVal = parts[0] + '.' + parts.slice(1).join('');
                    }
                    setRespostaFormAndMark(prev => ({
                      ...prev,
                      valor_impostos_display: displayVal,
                      valor_impostos: displayVal === '' ? 0 : parseFloat(numVal) || 0
                    }));
                  }}
                  onBlur={() => {
                    setRespostaFormAndMark(prev => ({
                      ...prev,
                      valor_impostos_display: undefined
                    }));
                  }}
                  className="pl-8"
                  placeholder="0,00"
                  disabled={!!respostaForm.impostos_inclusos}
                  data-testid="resposta-impostos"
                />
              </div>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="checkbox"
                  id="impostos-inclusos-cb"
                  checked={!!respostaForm.impostos_inclusos}
                  onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, impostos_inclusos: e.target.checked }))}
                  className="h-3.5 w-3.5"
                  data-testid="resposta-impostos-inclusos-cb"
                />
                <label htmlFor="impostos-inclusos-cb" className="text-xs text-gray-500 cursor-pointer">
                  Inclusos nos preços
                </label>
              </div>
            </div>
            <div>
              <Label>Desconto</Label>
              <CurrencyInput
                value={respostaForm.desconto || 0}
                onChange={(v) => setRespostaFormAndMark(prev => ({ ...prev, desconto: v }))}
                placeholder="0,00"
                currency={cotacao?.moeda || 'BRL'}
                data-testid="resposta-desconto"
              />
              <p className="text-xs text-gray-500 mt-1">Subtraído do total</p>
            </div>
            <div>
              <Label>Prazo de Entrega</Label>
              <Input
                value={respostaForm.prazo_entrega || ''}
                onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, prazo_entrega: e.target.value }))}
                placeholder="Ex: 10 dias úteis"
                data-testid="resposta-prazo-entrega"
              />
            </div>
            <div>
              <Label>Prazo Garantia</Label>
              <Input
                value={respostaForm.prazo_garantia}
                onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, prazo_garantia: e.target.value }))}
                placeholder="Ex: 2 anos"
                data-testid="resposta-prazo-garantia"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Assistência Técnica</Label>
              <Input
                value={respostaForm.assistencia_tecnica || ''}
                onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, assistencia_tecnica: e.target.value }))}
                placeholder="Ex: Autorizada local, 24h"
                data-testid="resposta-assistencia-tecnica"
              />
            </div>
          </div>

          {/* Condições de importação — o fornecedor confirma/sobrescreve os defaults da cotação */}
          {cotacao?.moeda && cotacao.moeda !== 'BRL' && (
            <div className="border-t pt-3">
              <Label className="mb-2 block text-sm font-semibold text-slate-700">Condições de importação (proposta do fornecedor)</Label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <Label>Incoterm</Label>
                  <Select
                    value={respostaForm.incoterm || 'none'}
                    onValueChange={(v) => setRespostaFormAndMark(prev => ({ ...prev, incoterm: v === 'none' ? '' : v }))}
                  >
                    <SelectTrigger data-testid="resposta-incoterm">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— Nenhum —</SelectItem>
                      {INCOTERMS.map(i => (
                        <SelectItem key={i.code} value={i.code}>{i.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Porto/Aeroporto de origem</Label>
                  <Input
                    value={respostaForm.porto_origem || ''}
                    onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, porto_origem: e.target.value }))}
                    placeholder="Ex: Shanghai (CNSHA)"
                    data-testid="resposta-porto-origem"
                  />
                </div>
                <div>
                  <Label>Transit Time (dias)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={respostaForm.transit_time_dias ?? ''}
                    onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, transit_time_dias: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    placeholder="Trânsito"
                    data-testid="resposta-transit-time"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-1">Pré-preenchido com os defaults da cotação. Ajuste se este fornecedor oferecer condições diferentes.</p>
            </div>
          )}

          {/* Logística / Frete — o fornecedor confirma peso, volumes e cubagem (base do frete) */}
          {cotacao?.moeda && cotacao.moeda !== 'BRL' && (
            <div className="border-t pt-3">
              <Label className="mb-2 block text-sm font-semibold text-slate-700">Logística / Frete (proposta do fornecedor)</Label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label>Peso Bruto (kg)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.001"
                    value={respostaForm.peso_bruto ?? ''}
                    onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, peso_bruto: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    placeholder="0,000"
                    data-testid="resposta-peso-bruto"
                  />
                </div>
                <div>
                  <Label>Peso Líquido (kg)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.001"
                    value={respostaForm.peso_liquido ?? ''}
                    onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, peso_liquido: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    placeholder="0,000"
                    data-testid="resposta-peso-liquido"
                  />
                </div>
                <div>
                  <Label>Qtd de Volumes</Label>
                  <Input
                    type="number"
                    min="0"
                    value={respostaForm.quantidade_volumes ?? ''}
                    onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, quantidade_volumes: e.target.value === '' ? '' : parseInt(e.target.value) }))}
                    placeholder="0"
                    data-testid="resposta-quantidade-volumes"
                  />
                </div>
                <div>
                  <Label>Volume total (CBM — m³)</Label>
                  <Input
                    type="number"
                    min="0"
                    step="0.0001"
                    value={respostaForm.volume_cbm ?? ''}
                    onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, volume_cbm: e.target.value === '' ? '' : parseFloat(e.target.value) }))}
                    placeholder="0,0000"
                    data-testid="resposta-volume-cbm"
                  />
                </div>
              </div>
              <div className="mt-3">
                <Label>Dimensões / observação de embalagem</Label>
                <Input
                  value={respostaForm.dimensoes_observacao || ''}
                  onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, dimensoes_observacao: e.target.value }))}
                  placeholder="Ex: 3 caixas 120×80×100 cm (paletizado)"
                  data-testid="resposta-dimensoes-observacao"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Pré-preenchido com a soma do cadastro dos produtos (quando houver). Peso bruto e CBM são a base para cotar o frete internacional.</p>
            </div>
          )}

          <div>
            <Label>Observações</Label>
            <Textarea
              value={respostaForm.observacao}
              onChange={(e) => setRespostaFormAndMark(prev => ({ ...prev, observacao: e.target.value }))}
              placeholder="Observações adicionais..."
              rows={3}
            />
          </div>

          {/* === ANEXOS DA RESPOSTA (Fase 2) === */}
          <div>
            <Label className="mb-2 block text-sm font-semibold">Anexos da Resposta</Label>
            <RespostaAnexosManager
              cotacaoId={cotacao.id}
              fornecedorId={selectedFornecedor}
            />
          </div>
        </>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onCancel} disabled={loading}>
          Cancelar
        </Button>
        <Button onClick={handleSubmit} disabled={loading || !selectedFornecedor}>
          {loading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Salvando...
            </>
          ) : (
            respostasAlteradas.size > 1 || (respostasAlteradas.size === 1 && !respostasAlteradas.has(selectedFornecedor))
              ? `Salvar ${respostasAlteradas.size + (respostasAlteradas.has(selectedFornecedor) ? 0 : 1)} Resposta(s)`
              : 'Salvar Resposta'
          )}
        </Button>
      </DialogFooter>

      {/* Dialog: Adicionar Fornecedor à Cotação */}
      <Dialog open={showAddFornecedor} onOpenChange={setShowAddFornecedor}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Adicionar Fornecedor à Cotação</DialogTitle>
          </DialogHeader>
          <FornecedorPicker
            fornecedores={todosFornecedores}
            jaVinculados={fornecedoresDaCotacao.map(f => f.fornecedor_id)}
            onSelect={handleAdicionarFornecedor}
            onCancel={() => setShowAddFornecedor(false)}
          />
        </DialogContent>
      </Dialog>

      {/* Preview do PDF da Resposta */}
      {pdfPreviewResposta && (
        <PdfPreviewDialog
          open={!!pdfPreviewResposta}
          onClose={() => setPdfPreviewResposta(null)}
          title={pdfPreviewResposta.title}
          apiPath={pdfPreviewResposta.path}
          filename={pdfPreviewResposta.filename}
        />
      )}

      {/* Extração de resposta com IA */}
      <ExtrairRespostaIADialog
        open={showExtrairIA}
        onClose={() => setShowExtrairIA(false)}
        cotacao={cotacao}
        fornecedorId={selectedFornecedor}
        fornecedorNome={getNomeFornecedor(selectedFornecedor)}
        onAplicar={aplicarExtracaoIA}
      />

      {/* Adotar alternativa inline */}
      <AvaliarAlternativaDialog
        avaliar={avaliarAlternativa}
        cotacaoId={cotacao?.id}
        onClose={() => setAvaliarAlternativa(null)}
        onAdotado={async () => {
          // Refetch e usa a cotação FRESCA para reconstruir o form
          const updatedCot = onCotacaoChange ? await onCotacaoChange() : null;
          if (selectedFornecedor) {
            const novaForm = construirFormParaFornecedor(selectedFornecedor, updatedCot);
            setRespostaForm(novaForm);
            // Limpa marcação "alterado" desse fornecedor (mudança veio do backend, já está persistida)
            setRespostasAlteradas(prev => {
              const ns = new Set(prev);
              ns.delete(selectedFornecedor);
              return ns;
            });
          }
        }}
      />
    </div>
  );
};

// Pequeno seletor de fornecedor com busca — usado no Dialog acima
const FornecedorPicker = ({ fornecedores, jaVinculados, onSelect, onCancel }) => {
  const [search, setSearch] = useState('');
  const filtrados = fornecedores.filter(f => {
    const nome = (getFornecedorNome(f) || '').toLowerCase();
    const email = (f.email || '').toLowerCase();
    const q = search.toLowerCase();
    return !q || nome.includes(q) || email.includes(q);
  });

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          placeholder="Buscar fornecedor por nome ou e-mail..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-8"
          data-testid="busca-fornecedor-add"
        />
      </div>
      <div className="max-h-72 overflow-y-auto border rounded">
        {filtrados.length === 0 ? (
          <p className="text-sm text-gray-500 p-4 text-center">Nenhum fornecedor encontrado</p>
        ) : (
          filtrados.map(f => {
            const ja = jaVinculados.includes(f.id);
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => !ja && onSelect(f)}
                disabled={ja}
                className={`w-full text-left p-3 border-b hover:bg-gray-50 transition-colors ${ja ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid={`forn-pick-${f.id}`}
              >
                <p className="font-medium text-sm">{getFornecedorNome(f)}</p>
                <p className="text-xs text-gray-500">{f.email || 'sem email'}</p>
                {ja && <span className="text-xs text-amber-600">já está nesta cotação</span>}
              </button>
            );
          })
        )}
      </div>
      <DialogFooter>
        <Button variant="outline" onClick={onCancel}>Fechar</Button>
      </DialogFooter>
    </div>
  );
};

export default RespostaFornecedorForm;
